import { countries as countriesData } from 'countries-list';

export interface Country {
    name: string;
    code: string;
    dialCode: string;
    pattern: string;
    placeholder: string;
    maxLength: number;
}

// Map the raw data to our format
const allCountries: Country[] = Object.entries(countriesData).map(([code, data]) => {
    const dialCode = `+${data.phone[0]}`;
    // Since countries-list doesn't provide patterns, we'll use a default one
    // and override common ones if needed.
    let pattern = "##########";
    let placeholder = "1234567890";
    let maxLength = 10;

    // Default overrides for common countries to keep UX nice
    if (code === 'IN') {
        pattern = "##### #####";
        placeholder = "98765 43210";
        maxLength = 10;
    } else if (code === 'US' || code === 'CA') {
        pattern = "### ### ####";
        placeholder = "201 555 0123";
        maxLength = 10;
    } else if (code === 'GB') {
        pattern = "#### ######";
        placeholder = "7700 900123";
        maxLength = 10;
    } else if (code === 'AE') {
        pattern = "## ### ####";
        placeholder = "50 123 4567";
        maxLength = 9;
    }

    return {
        name: data.name,
        code: code,
        dialCode: dialCode,
        pattern: pattern,
        placeholder: placeholder,
        maxLength: maxLength
    };
}).sort((a, b) => a.name.localeCompare(b.name));

export function getCountryOptions() {
    return allCountries.map(country => ({
        value: country.code,
        label: `${country.dialCode}`,
        fullLabel: `${country.name} (${country.dialCode})`,
        // Using country-flag-icons URL structure as requested indirectly by library mention
        flagUrl: `https://purecatamphetamine.github.io/country-flag-icons/3x2/${country.code}.svg`,
        dialCode: country.dialCode
    }));
}

export function getCountryByCode(code: string) {
    return allCountries.find(c => c.code === code) || allCountries.find(c => c.code === 'IN') || allCountries[0];
}

export function getPlaceholder(countryCode: string) {
    const country = getCountryByCode(countryCode);
    return country.placeholder;
}

export function getMaxLength(countryCode: string) {
    const country = getCountryByCode(countryCode);
    return country.maxLength;
}

export function formatPhoneNumber(value: string) {
    // Strip all non-digits
    return value.replace(/\D/g, '');
}

export function formatPhoneWithSpaces(digits: string, countryCode: string) {
    const country = getCountryByCode(countryCode);
    const pattern = country.pattern;

    let result = '';
    let digitIndex = 0;

    for (let i = 0; i < pattern.length && digitIndex < digits.length; i++) {
        if (pattern[i] === '#') {
            result += digits[digitIndex];
            digitIndex++;
        } else if (pattern[i] === ' ') {
            if (digitIndex < digits.length) {
                result += ' ';
            }
        } else {
            result += pattern[i];
        }
    }

    // If no pattern match (length > pattern), just show digits
    if (digitIndex < digits.length) {
        result += digits.slice(digitIndex);
    }

    return result;
}

export function validatePhoneNumber(phone: string, countryCode: string) {
    const digits = formatPhoneNumber(phone);
    const country = getCountryByCode(countryCode);

    if (!digits) {
        return { isValid: false, error: "Phone number is required" };
    }

    // Very basic validation since we don't have exact rules for 200+ countries
    if (digits.length < 5) {
        return { isValid: false, error: "Phone number is too short" };
    }

    const maxAllowed = Math.max(15, country.maxLength + 2);
    if (digits.length > maxAllowed) {
        return { isValid: false, error: "Phone number is too long" };
    }

    return { isValid: true };
}
